/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  init,
  browserProfilingIntegration,
  browserTracingIntegration,
  reactRouterV6BrowserTracingIntegration,
} from '@sentry/react';
import React from 'react';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router';
import { getCodeChallenge, getRandomString } from '@opengeoweb/authentication';
import { sentryPrivacyOptions } from '@opengeoweb/sentry';
import { sessionStorageProvider } from '@opengeoweb/shared';

// ---> Sentry related code
export const DEFAULT_SENTRY_DSN =
  'https://c2954983afcb2973d1f05b3256cafde7@o4508720349839360.ingest.de.sentry.io/4508720354754640';

export const INVALID_SENTRY_DSN_WARNING =
  'Sentry is not initialized: GW_FEATURE_SENTRY_DSN is not a valid Sentry DSN';

const isValidSentryDsn = (dsn: string): boolean => {
  try {
    const url = new URL(dsn);
    const projectId = url.pathname.split('/').pop() ?? '';
    return (
      (url.protocol === 'https:' || url.protocol === 'http:') &&
      url.username !== '' &&
      url.hostname !== '' &&
      /^\d+$/.test(projectId)
    );
  } catch {
    return false;
  }
};

export const getSentryDsn = (configuredDsn: unknown): string | undefined => {
  if (configuredDsn === undefined) {
    return DEFAULT_SENTRY_DSN;
  }
  if (
    typeof configuredDsn === 'string' &&
    isValidSentryDsn(configuredDsn.trim())
  ) {
    return configuredDsn.trim();
  }
  console.warn(INVALID_SENTRY_DSN_WARNING);
  return undefined;
};

export const initializeSentry = (
  configuredDsn: string | undefined,
  hostname: string = window.location.hostname,
): void => {
  const hasCustomDsn = configuredDsn !== undefined;
  const dsn = getSentryDsn(configuredDsn);
  if (!dsn) {
    return;
  }

  const sentryEnvironments = [
    {
      windowLocationPattern: /.*acc.opengeoweb.com.*/,
      sentryEnvironment: 'open-acc',
      sentryEnabled: true,
    },
    {
      windowLocationPattern: /.*dev.opengeoweb.com.*/,
      sentryEnvironment: 'open-dev',
      sentryEnabled: true,
    },
    {
      windowLocationPattern: /.*acc-geoweb.fmi.fi.*/,
      sentryEnvironment: 'fmi-acc',
      sentryEnabled: true,
    },
    {
      windowLocationPattern: /.*localhost*/,
      sentryEnvironment: 'test',
      sentryEnabled: true,
    },
  ];

  const currentEnvironment = sentryEnvironments.find((environment) =>
    environment.windowLocationPattern.exec(hostname),
  );

  const isEnabledForHost = currentEnvironment
    ? currentEnvironment.sentryEnabled
    : hasCustomDsn;

  if (isEnabledForHost) {
    init({
      environment: currentEnvironment?.sentryEnvironment ?? hostname, // Name Sentry environment after our environment
      dsn,
      integrations: [
        browserProfilingIntegration(),
        browserTracingIntegration(),
        reactRouterV6BrowserTracingIntegration({
          useEffect: React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
      ],
      ...sentryPrivacyOptions,
      // ___ Performance Monitoring ____
      tracesSampleRate: 1.0, //  Capture 100% of the transactions
      tracePropagationTargets: [], // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled. Example ['localhost', /^https:\/\/yourserver\.io\/api/]
      // ____ Session Replay _____
      replaysSessionSampleRate: 0, // This will disable automatic Replay creation. We want to start them manually when needed
      replaysOnErrorSampleRate: 0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
      profilesSampleRate: 1.0,
    });
  }
};
// Sentry related code <--

export const initializeSessionStorage = async (): Promise<void> => {
  if (!sessionStorageProvider.getOauthState()) {
    const oauthState = getRandomString();
    sessionStorageProvider.setOauthState(oauthState);
  }

  if (
    !sessionStorageProvider.getOauthCodeVerifier() ||
    !sessionStorageProvider.getOauthCodeChallenge()
  ) {
    const codeVerifier = getRandomString();
    await getCodeChallenge(codeVerifier).then((codeChallenge) => {
      // setting both codeChallenge and codeVerifier at the same time since they are related
      sessionStorageProvider.setOauthCodeVerifier(codeVerifier);
      sessionStorageProvider.setOauthCodeChallenge(codeChallenge);
    });
  }
};
